'use strict';
/* ==========================================================================
   core-local/balancekit.js — "Balance It": the antique two-pan balance, drawn in ink by code (DESIGN.md §2).
   One drawing for every scene (title, video, Red Pen, The Scale, the jingle, the thumb). Pure: a function of its
   arguments (no state, no time of its own); the tilt comes from the model (core-local/scale.js Scale.tilt).

   BalanceKit.draw(x, y, o) → the geometry {pivot: {x, y}, left: {x, y}, right: {x, y}, a} (pan centers = the pan
     dish's top middle, a = the beam's canvas angle in radians) so a scene can put things on / point at a pan.
     x, y: the middle of the base (it stands on y).
     o.s       scale (1 = a 880 px wide balance, 560 px tall)
     o.tilt    beam angle in DEGREES as Scale.tilt gives it: > 0 = the LEFT pan is lower (default 0 = level)
     o.left / o.right  what is on each pan: {boxes, bags, bagWeights, weights} (Scale.contents(eq).left / .right)
     o.v       the letter on the boxes (default 'x'); o.bagLabel (default "v + bagWeights")
     o.gray    fade the balance (the scale can't hold this equation: Scale.canHold false)
     o.alpha   overall alpha
     o.lift    {side: 'left' | 'right', weights: k, y: px}: k of that pan's weights drawn lifted by y px (a hand taking
               them off); they are not on the pan any more for the drawing's totals (the model decides the tilt)
   BalanceKit.weight(x, y, s) / BalanceKit.box(x, y, s, label) / BalanceKit.bag(x, y, s, label, weights): the pieces.
   BalanceKit.GEOM: the unscaled sizes (beam half-length, chain, pan width, pillar height).
   Uses the notebook pack (NB.PAL, NB.FONT) and the engine's g; no images, no shadows.
   ========================================================================== */
(function () {
  const GEOM = { beam: 290, chain: 170, pan: 300, pillar: 330, base: 300, box: 58, weight: 22 };
  const PALN = () => (window.NB && NB.PAL) || { ink: '#1d2b4f', pencil: '#737985', card: '#ffffff', redpen: '#d23f33', page: '#fcfcf8' };
  const HAND = () => (window.NB && NB.FONT && NB.FONT.hand) || 'Kalam, cursive';
  const UIF = () => (window.NB && NB.FONT && NB.FONT.ui) || 'sans-serif';

  function strokeFill(fill, lw, ink) { if (fill) { g.fillStyle = fill; g.fill(); } g.lineWidth = lw; g.strokeStyle = ink; g.stroke(); }

  /* a one-ounce weight: a small brass-ish cylinder in ink, stamped "1"; (x, y) = the bottom middle */
  function weight(x, y, s = 1) {
    const P = PALN(), w = GEOM.weight * s, h = GEOM.weight * 1.05 * s, e = w * 0.22;
    g.save(); g.translate(x, y);
    g.beginPath(); g.moveTo(-w / 2, -h + e); g.lineTo(-w / 2, -e); g.ellipse(0, -e, w / 2, e, 0, Math.PI, 0, true); g.lineTo(w / 2, -h + e); g.closePath();
    strokeFill('#e9dfb8', Math.max(1.2, 2.2 * s), P.ink);
    g.beginPath(); g.ellipse(0, -h + e, w / 2, e, 0, 0, Math.PI * 2); strokeFill('#f4ecc8', Math.max(1.2, 2.2 * s), P.ink);
    if (s > 0.45) { g.fillStyle = P.ink; g.font = `700 ${Math.round(13 * s)}px ${UIF()}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('1', 0, -h / 2 + e * 0.3); }
    g.restore();
    return { w, h };
  }
  /* a mystery box: a square labeled with the letter; (x, y) = the bottom middle */
  function box(x, y, s = 1, label = 'x') {
    const P = PALN(), b = GEOM.box * s;
    g.save(); g.translate(x, y);
    g.beginPath(); g.rect(-b / 2, -b, b, b); strokeFill(P.card || '#fff', Math.max(1.5, 3.5 * s), P.ink);
    g.beginPath(); g.moveTo(-b / 2, -b * 0.72); g.lineTo(b / 2, -b * 0.72); g.lineWidth = Math.max(1, 1.8 * s); g.strokeStyle = P.pencil; g.stroke();   // the lid's edge
    g.fillStyle = P.ink; g.font = `700 ${Math.round(34 * s)}px ${HAND()}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(label, 0, -b * 0.36);
    g.restore();
    return { w: b, h: b };
  }
  /* a paper gift bag holding one box and `weights` ounces (labeled "x + 2"); (x, y) = the bottom middle */
  function bag(x, y, s = 1, label = 'x + 2', weights = 0) {
    const P = PALN(), w = 84 * s, h = 92 * s;
    g.save(); g.translate(x, y);
    g.beginPath(); g.moveTo(-w / 2, 0); g.lineTo(-w * 0.44, -h); g.lineTo(w * 0.44, -h); g.lineTo(w / 2, 0); g.closePath();
    strokeFill('#f2e6d0', Math.max(1.5, 3 * s), P.ink);
    g.beginPath(); g.arc(0, -h, w * 0.2, Math.PI, 0); g.lineWidth = Math.max(1.2, 2.5 * s); g.strokeStyle = P.ink; g.stroke();   // the handle
    g.fillStyle = P.ink; g.font = `700 ${Math.round(24 * s)}px ${HAND()}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(label, 0, -h * 0.45);
    // the tissue weights, as small dots along the bottom (counted by the label)
    for (let i = 0; i < Math.min(weights, 6); i++) { g.beginPath(); g.arc((i - (Math.min(weights, 6) - 1) / 2) * 11 * s, -h * 0.14, 3.2 * s, 0, Math.PI * 2); g.fillStyle = P.pencil; g.fill(); }
    g.restore();
    return { w, h };
  }

  /* what sits on one pan, arranged: boxes / bags in rows of three on the left (stacked upward), weights in rows of five
     on the right (stacked upward, so they can be counted); everything shrinks together if it would overhang the pan */
  function load(cx, cy, s, c, v, bagLabel, lifted) {
    if (!c) return;
    const boxes = c.boxes || 0, bags = c.bags || 0, wts = Math.max(0, (c.weights || 0) - (lifted ? lifted.weights : 0));
    const pw = GEOM.pan * s, items = boxes + bags, ww = GEOM.weight * s, rowW = 5 * ww * 1.04;
    const itemW = bags ? 84 * s : GEOM.box * s, itemH = bags ? 96 * s : GEOM.box * s, gap = 6 * s, perRow = Math.min(3, items);
    const leftW = items ? perRow * itemW + (perRow - 1) * gap : 0;
    const total = leftW + (wts ? rowW + (items ? 14 * s : 0) : 0);
    const k = Math.min(1, (pw * 0.94) / Math.max(1, total));            // shrink to fit the pan
    const x0 = cx - (total * k) / 2;
    for (let i = 0; i < items; i++) {
      const row = Math.floor(i / 3), col = i % 3, inRow = Math.min(3, items - row * 3);
      const ix = x0 + ((perRow - inRow) * (itemW + gap) / 2 + col * (itemW + gap) + itemW / 2) * k, iy = cy - row * (itemH + 2 * s) * k;
      if (i < boxes) box(ix, iy, s * k, v); else bag(ix, iy, s * k, bagLabel, c.bagWeights || 0);
    }
    if (wts) {
      const wx0 = x0 + (leftW + (items ? 14 * s : 0)) * k, wh = GEOM.weight * 1.02 * s * k;
      for (let i = 0; i < wts; i++) {
        const row = Math.floor(i / 5), col = i % 5;
        weight(wx0 + (col + 0.5) * ww * 1.04 * k, cy - row * wh, s * k);
      }
    }
    if (lifted && lifted.weights > 0) {
      for (let i = 0; i < lifted.weights; i++) weight(cx - (lifted.weights - 1) * ww * 0.55 + i * ww * 1.1, cy - lifted.y, s);
    }
  }

  function draw(x, y, o = {}) {
    const P = PALN(), s = o.s || 1, ink = P.ink, lw = 5 * s;
    const a = -((o.tilt || 0) * Math.PI) / 180;
    const B = GEOM.beam * s, H = GEOM.pillar * s, ch = GEOM.chain * s, pw = GEOM.pan * s;
    const px = x, py = y - H - 36 * s;
    g.save();
    g.globalAlpha *= (o.alpha == null ? 1 : o.alpha) * (o.gray ? 0.4 : 1);
    // base + pillar
    g.beginPath(); g.moveTo(x - GEOM.base * s / 2, y); g.lineTo(x + GEOM.base * s / 2, y); g.lineTo(x + GEOM.base * s * 0.36, y - 34 * s); g.lineTo(x - GEOM.base * s * 0.36, y - 34 * s); g.closePath();
    strokeFill('#e9e3cf', lw, ink);
    g.beginPath(); g.rect(x - 16 * s, py, 32 * s, H + 2 * s); strokeFill('#f3efe0', lw, ink);
    for (let k = 1; k <= 4; k++) { g.beginPath(); g.moveTo(x - 16 * s, py + k * H / 5); g.lineTo(x + 16 * s, py + k * H / 5); g.lineWidth = 1.5 * s; g.strokeStyle = P.pencil; g.stroke(); }
    // the dial above the pivot, and the pointer (turns with the beam)
    const dr = 64 * s;
    g.beginPath(); g.arc(px, py - 8 * s, dr, Math.PI * 1.15, Math.PI * 1.85); g.lineWidth = 3 * s; g.strokeStyle = ink; g.stroke();
    for (let k = -3; k <= 3; k++) {
      const t = -Math.PI / 2 + k * 0.1;
      g.beginPath(); g.moveTo(px + Math.cos(t) * dr, py - 8 * s + Math.sin(t) * dr); g.lineTo(px + Math.cos(t) * (dr - (k ? 10 : 18) * s), py - 8 * s + Math.sin(t) * (dr - (k ? 10 : 18) * s));
      g.lineWidth = (k ? 2 : 3.5) * s; g.strokeStyle = k ? P.pencil : ink; g.stroke();
    }
    g.save(); g.translate(px, py); g.rotate(a);
    g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -dr + 4 * s); g.lineWidth = 3.5 * s; g.strokeStyle = P.redpen || '#d23f33'; g.stroke();
    // the beam
    g.beginPath(); g.moveTo(-B, -7 * s); g.lineTo(B, -7 * s); g.lineTo(B, 7 * s); g.lineTo(-B, 7 * s); g.closePath(); strokeFill('#e9dfb8', lw, ink);
    g.beginPath(); g.arc(-B, 0, 9 * s, 0, Math.PI * 2); strokeFill('#f4ecc8', 3 * s, ink);
    g.beginPath(); g.arc(B, 0, 9 * s, 0, Math.PI * 2); strokeFill('#f4ecc8', 3 * s, ink);
    g.restore();
    g.beginPath(); g.arc(px, py, 14 * s, 0, Math.PI * 2); strokeFill('#f4ecc8', 4 * s, ink);
    // the pans hang straight down from the beam's ends on three chains
    const ends = [-1, 1].map(k => ({ x: px + k * B * Math.cos(a), y: py + k * B * Math.sin(a) }));
    const pans = ends.map(e => ({ x: e.x, y: e.y + ch }));
    pans.forEach(p => {
      g.save(); g.setLineDash([7 * s, 5 * s]); g.lineWidth = 2.5 * s; g.strokeStyle = ink;
      g.beginPath(); g.moveTo(p.x, p.y - ch); g.lineTo(p.x - pw / 2 + 12 * s, p.y); g.moveTo(p.x, p.y - ch); g.lineTo(p.x + pw / 2 - 12 * s, p.y); g.moveTo(p.x, p.y - ch); g.lineTo(p.x, p.y - 10 * s); g.stroke();
      g.restore();
    });
    // what's on the pans (in front of the chains, behind the dish rim)
    const lift = o.lift || null;
    load(pans[0].x, pans[0].y - 2 * s, s, o.left, o.v || 'x', o.bagLabel || `${o.v || 'x'} + ${(o.left && o.left.bagWeights) || (o.right && o.right.bagWeights) || 0}`, lift && lift.side === 'left' ? lift : null);
    load(pans[1].x, pans[1].y - 2 * s, s, o.right, o.v || 'x', o.bagLabel || `${o.v || 'x'} + ${(o.right && o.right.bagWeights) || (o.left && o.left.bagWeights) || 0}`, lift && lift.side === 'right' ? lift : null);
    // the dishes
    pans.forEach(p => {
      g.beginPath(); g.moveTo(p.x - pw / 2, p.y); g.quadraticCurveTo(p.x, p.y + 46 * s, p.x + pw / 2, p.y); g.closePath();
      strokeFill('#e9dfb8', lw, ink);
    });
    g.restore();
    return { pivot: { x: px, y: py }, left: pans[0], right: pans[1], a };
  }

  window.BalanceKit = { draw, weight, box, bag, GEOM };
})();
